import React, { useEffect, useState, useMemo } from 'react';
import { Provider } from 'react-redux';
import { QueryClientProvider, QueryClient } from 'react-query';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import { createBypassTheme } from '../shared/utils/createBypassTheme';
import { BypassThemeProvider } from '../shared/utils/BypassThemeProvider';
import { initEmotionThemeOverride } from '../shared/utils/emotionThemeOverride';
import { initEmotionHotfix } from '../shared/utils/emotionHotfix';
import emotionCache from '../shared/utils/emotionCache';

// Routing
// react-toastify's base stylesheet. Without it the toast container renders with
// no positioning or background, so every toast in the app is invisible -- the
// call succeeds and the user sees nothing. It was previously imported only by
// VerifyOTP.tsx, which is lazily loaded, so toasts only worked on that one
// route. Must come BEFORE custom.css, which overrides these rules.
import { restoreRememberedSession } from '../shared/utils/authStorage';

// Rehydrate a "Remember me" session before anything reads sessionStorage.
// Module scope, not an effect: route guards run on first render.
restoreRememberedSession();

import 'react-toastify/dist/ReactToastify.css';
import '../components/common/custom.css';
import AppRoutes from './routes';

// Project imports
import NavigationScroll from '../components/layout/NavigationScroll';
import { ToastContainer, Flip } from 'react-toastify';
import { store } from '../shared/store/index';

// Initialize emotion theme override BEFORE any components render
initEmotionThemeOverride();
// Initialize aggressive hotfix for theme errors
initEmotionHotfix();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

const App: React.FC = () => {
  // Removed offline detection - not needed for this application

  // Create a bypass theme to completely avoid emotion's attachTheme
  const appTheme = useMemo(() => {
    return createBypassTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#5e35b1',
          light: '#9575cd',
          dark: '#4527a0'
        },
        secondary: {
          main: '#ff6f00',
          light: '#ffb74d',
          dark: '#e65100'
        }
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <CacheProvider value={emotionCache}>
          <StyledEngineProvider injectFirst>
            <BypassThemeProvider theme={appTheme}>
              <CssBaseline />
              <NavigationScroll>
                <AppRoutes />
              </NavigationScroll>
            </BypassThemeProvider>
          </StyledEngineProvider>
        </CacheProvider>
      </Provider>
      {/* React Query DevTools removed - was showing as info icon at bottom of pages */}
      <ToastContainer
        transition={Flip}
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        limit={3}
        icon={false}
      />
    </QueryClientProvider>
  );
};

export default App;
